import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { citiesApi, formatPrice } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { MapPin, Building2, TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';

export const CitiesPage = () => {
  const { t, language } = useLanguage();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const response = await citiesApi.list();
      setCities(response.data || []);
    } catch (error) {
      console.error('Failed to load cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const cityImages = {
    'Alger': 'https://images.unsplash.com/photo-1675610137184-40f1bce58d71?w=800',
    'Oran': 'https://images.unsplash.com/photo-1706002027900-a1be40d0627e?w=800',
    'Béjaïa': 'https://images.unsplash.com/photo-1630838788292-d9c78b6a0f2f?w=800',
    'Tizi Ouzou': 'https://images.unsplash.com/photo-1605692024639-04377ef2fd90?w=800',
    'Annaba': 'https://images.unsplash.com/photo-1760726743931-24bd760fe554?w=800',
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const citiesSeoTitle = language === 'fr' ? 'Guide des villes - Immobilier en Algerie | Beytam' : 'دليل المدن - العقارات في الجزائر | بيتم';
  const citiesSeoDesc = language === 'fr' ? 'Decouvrez les prix immobiliers par ville en Algerie. Guides detailles : Alger, Oran, Bejaia, Tizi Ouzou, Annaba.' : 'اكتشف أسعار العقارات حسب المدينة في الجزائر. أدلة مفصلة: الجزائر، وهران، بجاية، تيزي وزو، عنابة.';

  useSEO({
    title: citiesSeoTitle,
    description: citiesSeoDesc,
    ogTitle: citiesSeoTitle,
    canonical: window.location.href,
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Skeleton className="h-12 w-64 mb-4" />
        <Skeleton className="h-6 w-96 mb-12" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cities-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="font-manrope text-4xl md:text-5xl font-bold mb-4">
            {t('cities_title')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            {t('cities_subtitle')}
          </p>
        </div>
      </div>

      {/* Cities grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {cities.map((city) => (
            <Card 
              key={city.city} 
              className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow"
              data-testid={`city-detail-${city.city.toLowerCase()}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="md:w-2/5 relative">
                  <img
                    src={cityImages[city.city] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'}
                    alt={city.city}
                    className="w-full h-48 md:h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-primary-500 font-semibold">
                      {city.total_listings} {t('cities_listings')}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <CardContent className="md:w-3/5 p-6">
                  <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-2">
                    {city.city}
                  </h2>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {language === 'ar' && city.description_ar ? city.description_ar : city.description}
                  </p>

                  {/* Price stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">
                        {language === 'fr' ? 'Prix/m² (vente)' : 'السعر/م² (بيع)'}
                      </p>
                      <p className="font-manrope font-bold text-primary-500">
                        {formatPrice(city.avg_price_sqm_sale)}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">
                        {language === 'fr' ? 'Prix/m² (location)' : 'السعر/م² (إيجار)'}
                      </p>
                      <p className="font-manrope font-bold text-accent-500">
                        {formatPrice(city.avg_price_sqm_rent)}/mois
                      </p>
                    </div>
                  </div>

                  {/* Neighborhoods preview */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">{t('cities_neighborhoods')}</p>
                    <div className="flex flex-wrap gap-2">
                      {city.neighborhoods.slice(0, 4).map((n) => (
                        <Badge key={n.name} variant="secondary" className="flex items-center gap-1">
                          {n.name}
                          {getTrendIcon(n.trend)}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Link to={`/search?city=${city.city}`} className="flex-1">
                      <Button variant="outline" className="w-full">
                        {language === 'fr' ? 'Voir les annonces' : 'عرض الإعلانات'}
                      </Button>
                    </Link>
                    <Link to={`/cities/${city.city.toLowerCase()}`} className="flex-1">
                      <Button className="w-full bg-primary-500">
                        {t('cities_guide')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
